/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useRef} from 'react';
import {GenericObject} from '@ion/core/src/types/entities';
import {UsePaginationResult} from '@ion/query/src/hooks/usePagination';
import {DEFAULT_PAGINATION} from './signals/paginationState';
import {useJobPagination} from './useJobPagination';

export interface WrapLoadItemsOptions {
  onBeforeLoad?: () => void;
  onSettled?: () => void;
};

export interface UseJobGridPaginationParams {
  activeTab: string;
  tabPaging: UsePaginationResult;
  // repositions ag-grid's own page pointer (0-indexed). Optional: pagination
  // still works without it, but stays one seeded fetch behind the real page.
  gotoPage?: (page: number) => void;
  // discards ag-grid's own row cache so it re-requests from startRow 0.
  // Optional, but without it a persisted page the current result set no
  // longer has cannot be recovered from - see the clamp in wrapLoadItems.
  purgeCache?: () => void;
};

export interface UseJobGridPaginationResult {
  wrapLoadItems: (loadItems: Function, options?: WrapLoadItemsOptions) => (params: GenericObject) => any;
};

/**
 * Custom hook: useJobGridPagination
 *
 * Records the grid's own pagination (page/pageSize, driven entirely by
 * IonGrid/ag-grid itself - forward/back arrows, typed page, page-size
 * dropdown) into the tab's persisted signal, purely so re-entering the tab
 * later can restore the last-viewed page.
 *
 * A tab switch always resets ag-grid's own datasource, so on the first fetch
 * since activeTab last changed, ag-grid believes it is asking for its own
 * "page 1" no matter which real page this tab was left on. wrapLoadItems
 * seeds that one fetch with the tab's real, persisted page directly, then -
 * if that seed differs from what ag-grid expected - asks gotoPage to move
 * ag-grid's own pointer to the real page too, deferred so it never calls
 * ag-grid's pagination API from inside ag-grid's own getRows callback. Once
 * gotoPage's own follow-up fetch lands, ag-grid's own addressing is correct
 * on its own, so every fetch after that trusts ag-grid's counter untouched.
 *
 * totalCount is always passed through untouched, even on the seeded fetch -
 * rewriting it to look like a short block is over made ag-grid believe the
 * real next page did not exist, which made it silently refuse gotoPage's
 * own repositioning. The seeded fetch may render a brief, real block-address
 * mismatch (e.g. blank padding for a short page) until gotoPage's follow-up
 * fetch replaces it moments later.
 *
 * A persisted page can also outlive the result set that had it, because some
 * filters are module-wide rather than per tab. ag-grid clamps gotoPage
 * against its own row count, so an over-range seed would otherwise strand the
 * grid on an empty block it can never navigate off. The seeded fetch's own
 * totalCount is the first honest bound available, so wrapLoadItems clamps
 * against it there, re-seeds off the clamped page and asks purgeCache to make
 * ag-grid request again from startRow 0.
 *
 * ```typescript
 *
 *  import {useJobGridPagination} from './useJobGridPagination';
 *
 *  const {wrapLoadItems} = useJobGridPagination({
 *    activeTab,
 *    tabPaging,
 *    gotoPage   : (page) => gridApiRef.current?.paginationGoToPage(page),
 *    purgeCache : () => gridApiRef.current?.purgeInfiniteCache?.()
 *  });
 *
 *  const wrappedLoadItems = wrapLoadItems(loadItems);
 *
 * ```
 *
 * @param activeTab the tab whose pagination is being recorded/seeded
 * @param tabPaging the react-query pagination state driving the grid's UI
 * @param gotoPage repositions ag-grid's own page pointer to match a seed
 * @param purgeCache makes ag-grid re-request rows from startRow 0
 */
export const useJobGridPagination = ({
  activeTab,
  tabPaging,
  gotoPage,
  purgeCache
}: UseJobGridPaginationParams): UseJobGridPaginationResult => {
  const {setPaginationForTab, getPaginationForTab} = useJobPagination();
  const hasSeededPageRef = useRef(false);
  // tracks the freshest activeTab so a fetch invoked late (dispatched before
  // a tab switch, resolving after it) can detect it is stale and no-op,
  // rather than seed/persist the page for the wrong tab...
  const activeTabRef = useRef(activeTab);
  // usePagination reports page 1 on mount regardless of the seeded page,
  // until the first setTotal() lands - recording that would stomp the real
  // persisted page. Skipping just this first invocation is enough, since the
  // effect re-fires with the corrected page once that first fetch resolves...
  const isFirstRecordRef = useRef(true);
  // when set, the next seeded fetch prefers this page over the persisted one.
  // Writing a clamp straight back into the signal would not survive:
  // usePaginatedQueryCache already applied the over-range page, so the record
  // effect below re-persists it before the re-seeded fetch ever reads it...
  const seedOverrideRef = useRef<number | null>(null);
  // one clamp per tab activation, so a result set that keeps coming back
  // short can never bounce the grid between purge and re-seed forever...
  const hasClampedRef = useRef(false);

  useEffect(() => {
    activeTabRef.current     = activeTab;
    hasSeededPageRef.current = false;
    seedOverrideRef.current  = null;
    hasClampedRef.current    = false;
  }, [activeTab]);

  // deliberately excludes activeTab - permanent tabs share one tabPaging
  // instance, so a mere tab switch must not re-record it into the newly
  // active tab's signal entry; only a genuine page/pageSize change should...
  useEffect(() => {
    if (isFirstRecordRef.current) {
      isFirstRecordRef.current = false;
    } else {
      setPaginationForTab(activeTab, {
        page     : tabPaging.page,
        pageSize : tabPaging.pageSize
      });
    }
  }, [tabPaging.page, tabPaging.pageSize, setPaginationForTab]);

  const wrapLoadItems = useCallback((
    loadItems: Function,
    options?: WrapLoadItemsOptions
  ) => function(params: GenericObject) {
    const {
      callback,
      pageSize: paramPageSize = DEFAULT_PAGINATION.pageSize,
      page,
      ...rest
    } = params;
    const requestTab = activeTab;
    let request;
    if (requestTab === activeTabRef.current) {
      const isFirstFetch = !hasSeededPageRef.current;
      hasSeededPageRef.current = true;
      const nativePageId = page + 1;
      const seededPageId = seedOverrideRef.current ??
        getPaginationForTab(requestTab)?.page ??
        nativePageId;
      const pageId = isFirstFetch ? seededPageId : nativePageId;
      if (isFirstFetch) {
        seedOverrideRef.current = null;
      }
      // only mismatched if the seed differs from what ag-grid's own startRow-
      // based addressing expected (e.g. a fresh mount already on page 1 isn't)...
      const isMismatched = isFirstFetch && pageId !== nativePageId;
      options?.onBeforeLoad?.();
      const wrappedCallback = (result, opts) => {
        const lastPage = Math.max(1, Math.ceil((result?.totalCount ?? 0) / paramPageSize));
        // the seed outlived its result set, so re-seed off the last page that
        // does exist rather than leaving ag-grid on an unnavigable block...
        const isOverRange = isMismatched && pageId > lastPage && !hasClampedRef.current;
        if (isOverRange) {
          hasClampedRef.current    = true;
          seedOverrideRef.current  = lastPage;
          hasSeededPageRef.current = false;
        }
        callback(result, opts);
        if (isOverRange) {
          // gotoPage cannot express a clamp back to page 1, since ag-grid is
          // already on its own page 0 - only a purge re-asks from startRow 0...
          setTimeout(() => {
            purgeCache?.();
          }, 0);
        } else if (isMismatched) {
          // ag-grid only learns the real row count from the callback above -
          // gotoPage before that finds no known row count, so it's a no-op...
          const targetPage = pageId;
          setTimeout(() => {
            gotoPage?.(targetPage - 1);
          }, 0);
        }
        options?.onSettled?.();
      };
      request = loadItems({...rest, page, pageId, pageSize: paramPageSize, callback: wrappedCallback});
    } else {
      callback?.({items: [], totalCount: 0}, {});
    }
    return request;
  }, [activeTab, getPaginationForTab, gotoPage, purgeCache]);

  return {
    wrapLoadItems
  };
};

export default useJobGridPagination;
